import { ref, onMounted, onUnmounted, watch, type ComputedRef, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useBotUser } from '@/stores/useBotUser';
import { useConfig } from '@/stores/useConfig';
import { trackArticle } from '@/common/api/api.pleased';
import { useMockedFlowStore } from '@/library/cf-v1/mocked-flow';
import { logger } from '@/common/api/logger';
import { parseErrorMessage } from '@/common/error';

interface UseArticleMetricsOptions {
  articleId: ComputedRef<string | number | undefined>;
  canConnectToSupport: ComputedRef<boolean>;
  abValue: ComputedRef<string>;
}

interface SaveArticleMetricsArgs {
  endChat?: boolean;
  restartChat?: boolean;
}

// ============================================================================
// Internal Helper Functions
// ============================================================================

/**
 * Calculates how many seconds have passed since the start timestamp.
 * This is used to track how long a user has been viewing an article.
 *
 * @param {number} startTimestamp - The time when viewing started (in milliseconds)
 * @returns {number} The number of seconds that have passed
 *
 * @example
 * ```ts
 * const startTime = Date.now();
 * // User views article for a while...
 * const secondsViewed = calculateDuration(startTime); // e.g., 45
 * ```
 */
const calculateDuration = (startTimestamp: number): number => {
  return Math.floor((Date.now() - startTimestamp) / 1000);
};

/**
 * Creates a function that saves article viewing metrics to the server.
 * This function takes all the necessary data (article ID, user info, duration, etc.)
 * and creates a save function that can be called later. The save function will
 * calculate the viewing duration and send it to the tracking API.
 *
 * @param {Object} options - All the data needed to save metrics
 * @param {ComputedRef<string | number | undefined>} options.articleId - The ID of the article being viewed
 * @param {ComputedRef<boolean>} options.canConnectToSupport - Whether the user can connect to support
 * @param {ComputedRef<string>} options.abValue - The AB testing value for this article
 * @param {Ref<number>} options.articleStartReadTs - When the user started viewing (timestamp)
 * @param {string} options.userId - The user's ID
 * @param {string | undefined} options.country - The user's country code
 *
 * @returns {Function} A function that can be called to save the metrics
 *
 * @example
 * ```ts
 * const saveMetrics = createSaveMetricsFunction({
 *   articleId,
 *   canConnectToSupport,
 *   abValue,
 *   articleStartReadTs,
 *   userId: 'user123',
 *   country: 'US'
 * });
 *
 * // Later, save the metrics
 * await saveMetrics({ endChat: true });
 * ```
 */
const createSaveMetricsFunction = (options: {
  articleId: ComputedRef<string | number | undefined>;
  canConnectToSupport: ComputedRef<boolean>;
  abValue: ComputedRef<string>;
  articleStartReadTs: Ref<number>;
  userId: string;
  country: string | undefined;
}) => {
  const { articleId, canConnectToSupport, abValue, articleStartReadTs, userId, country } = options;

  /**
   * Saves the article viewing metrics to the server.
   * This function calculates how long the user viewed the article and sends
   * that information along with other details (AB test value, support availability, etc.)
   * to the tracking API. It won't save if the duration is 0 or if there's no article ID.
   *
   * @param {Object} [args={}] - Optional flags for special cases
   * @param {boolean} [args.endChat] - Set to true if the user ended the chat
   * @param {boolean} [args.restartChat] - Set to true if the user restarted the chat
   *
   * @returns {Promise<void>} A promise that resolves when the save is complete
   */
  const saveArticleMetrics = async (args: SaveArticleMetricsArgs = {}) => {
    if (!articleId.value) return;

    const duration = calculateDuration(articleStartReadTs.value);

    if (duration === 0) return;

    const { endChat, restartChat } = args;

    try {
      await trackArticle({
        articleId: String(articleId.value),
        duration,
        abValue: abValue.value,
        connectToAgent: canConnectToSupport.value,
        country,
        userId,
        endChat,
        restartChat,
      });
    } catch (error) {
      logger.error('[USE_ARTICLE_METRICS] Failed to track article metrics', { error: parseErrorMessage(error) });
    }
  };

  return saveArticleMetrics;
};

/**
 * Creates a timer management system for tracking article viewing time.
 * This function sets up automatic tracking that:
 * - Resets the timer when the component mounts (starts tracking)
 * - Saves metrics when the component unmounts (user leaves)
 * - Saves metrics and resets timer when the article ID changes (user views different article)
 *
 * @param {Object} options - Configuration for the timer manager
 * @param {ComputedRef<string | number | undefined>} options.articleId - The article ID to watch for changes
 * @param {Ref<number>} options.articleStartReadTs - Reference to the start timestamp
 * @param {Function} options.saveArticleMetrics - The function to call when saving metrics
 *
 * @returns {Object} An object with a resetTimer function
 *
 * @example
 * ```ts
 * const { resetTimer } = createTimerManager({
 *   articleId,
 *   articleStartReadTs,
 *   saveArticleMetrics
 * });
 *
 * // Manually reset the timer if needed
 * resetTimer();
 * ```
 */
const createTimerManager = (options: {
  articleId: ComputedRef<string | number | undefined>;
  articleStartReadTs: Ref<number>;
  saveArticleMetrics: (args?: SaveArticleMetricsArgs) => Promise<void>;
}) => {
  const { articleId, articleStartReadTs, saveArticleMetrics } = options;

  const mockedFlowStore = useMockedFlowStore();
  const { isActive: isMockedFlowActive } = storeToRefs(mockedFlowStore);

  /**
   * Resets the viewing start time to right now.
   * Call this when you want to start tracking from a new point in time.
   *
   * @returns {void}
   */
  const resetTimer = () => {
    articleStartReadTs.value = Date.now();
  };

  watch(
    () => articleId.value,
    (newId, oldId) => {
      if (oldId && oldId !== newId) {
        saveArticleMetrics();
        resetTimer();
      }
    }
  );

  onUnmounted(() => {
    if (isMockedFlowActive.value) return;
    saveArticleMetrics();
  });

  onMounted(() => {
    if (isMockedFlowActive.value) return;
    resetTimer();
  });

  return {
    resetTimer,
  };
};

// ============================================================================
// Main Composable
// ============================================================================

/**
 * Tracks how long users spend viewing articles and saves that information.
 * This composable automatically starts tracking when the component loads and
 * saves the metrics when the user leaves or switches to a different article.
 * It tracks things like viewing duration, AB test values, and support availability.
 *
 * @param {UseArticleMetricsOptions} options - Configuration options
 * @param {ComputedRef<string | number | undefined>} options.articleId - The ID of the article being viewed
 * @param {ComputedRef<boolean>} options.canConnectToSupport - Whether the user can connect to support
 * @param {ComputedRef<string>} options.abValue - The AB testing value for this article
 *
 * @returns {Object} An object with two functions:
 *   - saveArticleMetrics: Manually save metrics (useful when user ends chat or restarts)
 *   - resetTimer: Reset the viewing start time to now
 *
 * @example
 * ```ts
 * // In your component
 * const articleId = computed(() => route.params.id);
 * const { canConnectToSupport } = useArticleMetadata({ articleId });
 * const abValue = computed(() => articleConfig.value?.abValue || '');
 *
 * const { saveArticleMetrics, resetTimer } = useArticleMetrics({
 *   articleId,
 *   canConnectToSupport,
 *   abValue,
 * });
 *
 * // When user ends chat, save with endChat flag
 * const handleEndChat = async () => {
 *   await saveArticleMetrics({ endChat: true });
 * };
 *
 * // If needed, reset the timer manually
 * resetTimer();
 * ```
 */
export const useArticleMetrics = (options: UseArticleMetricsOptions) => {
  const { articleId, canConnectToSupport, abValue } = options;

  const { user } = storeToRefs(useBotUser());
  const { config } = storeToRefs(useConfig());

  const articleStartReadTs = ref(Date.now());

  const saveArticleMetrics = createSaveMetricsFunction({
    articleId,
    canConnectToSupport,
    abValue,
    articleStartReadTs,
    userId: user.value.foreignId,
    country: config.value?.country,
  });

  const { resetTimer } = createTimerManager({
    articleId,
    articleStartReadTs,
    saveArticleMetrics,
  });

  return {
    saveArticleMetrics,
    resetTimer,
  };
};
